import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from 'react';
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import axiosInstance from '../../../../utils/axios';

export default function ExcelUploader({
  file,
  setFile,
  data,
  excelInfo,
  setExcelInfo,
  typeOfTheDividend,
}) {
  const [previewData, setPreviewData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [excelFileError, setExcelFileError] = useState(false);
  const [listOfYear,setListOfYear] = useState([]);

  useEffect(() => {
  axiosInstance
    .get("/form-year-type/list")
    .then((res) => {
      setListOfYear(res.data.payload);
    })
    .catch(console.error);
}, []);



  const dividendOptions = useMemo(() => {
    switch (typeOfTheDividend) {
      case "Cash":
        return [
          { value: "CashDividend_9_1", label: "Cash Dividend" },
          { value: "PSM_9_4", label: "Public Subscription Money" },
          { value: "FractionDividend_9_1", label: "Fraction Dividend" },
        ];

      case "Stock":
        return [
          { value: "BonusShare_9_2", label: "Stock Bonus Share" },
          { value: "RightShare_9_3", label: "Stock Right Share" },
          { value: "DividendOnDividend_9_2", label: "Dividend On Dividend" },
        ];

      case "FC":
        return [
          { value: "FC_Dividend_9_1", label: "FC Cash Dividend" },
          { value: "FC_PSM_9_4", label: "FC Public Subscription Money" },
        ];

      default:
        return [
          { value: "CashDividend_9_1", label: "Cash Dividend" },
          { value: "PSM_9_4", label: "Public Subscription Money" },
          { value: "FractionDividend_9_1", label: "Fraction Dividend" },
          { value: "BonusShare_9_2", label: "Stock Bonus Share" },
          { value: "RightShare_9_3", label: "Stock Right Share" },
          { value: "DividendOnDividend_9_2", label: "Dividend On Dividend" },
          { value: "FC_Dividend_9_1", label: "FC Cash Dividend" },
          { value: "FC_PSM_9_4", label: "FC Public Subscription Money" },
        ];
    }
  }, [typeOfTheDividend]);

  const handleExcelUpload = (e) => {
    const selected = e.target.files[0];

    if (!selected) return;

    const extension = selected.name.split(".").pop().toLowerCase();

    if (!["xlsx", "xls"].includes(extension)) {
      Swal.fire({
        icon: "warning",
        customClass: {
          container: 'my-swal'
        },
        title: "Invalid File",
        text: "Please upload only Excel files.",
      });
      return;
    }

    if (selected.size / 1024 / 1024 > 10) {
      Swal.fire({
        icon: "warning",
        title: "Maximum file size is 10 MB",
        customClass: {
          container: 'my-swal'
        },
      });
      return;
    }

    setFile(selected);

    const reader = new FileReader();

    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, {
        type: "binary",
      });

      const ws = wb.Sheets[wb.SheetNames[0]];

      const json = XLSX.utils.sheet_to_json(ws, {
        defval: "",
      });

      setPreviewData(json.slice(0, 100));

      if (json.length) {
        setHeaders(Object.keys(json[0]));
      } else {
        setHeaders([]);
      }
    };

    reader.readAsBinaryString(selected);
  };

  const removeFile = () => {
    Swal.fire({
      title: "Remove Excel File?",
      icon: "question",
      showCancelButton: true,
      customClass: {
          container: 'my-swal'
        },
    }).then((res) => {
      if (res.isConfirmed) {
        setFile(null);
        setPreviewData([]);
        setHeaders([]);
      }
    });
  };

  const handleUpload = async () => {
  if (!file) {
    Swal.fire({
      icon: "warning",
      title: "Please select an Excel file",
    });
    return;
  }

  if (!excelInfo.dividendYear) {
    Swal.fire({
      icon: "warning",
      title: "Please select Dividend Year",
    });
    return;
  }

  if (!excelInfo.dividendType) {
    Swal.fire({
      icon: "warning",
      title: "Please select Dividend Type",
    });
    return;
  }

  setIsLoading(true);
  setIsSuccess(false);
  setIsError(false);
  setExcelFileError(false);

  const formData = new FormData();

  formData.append("excelFiles", file);
  formData.append("inwarddividendType", excelInfo.dividendType);
  formData.append("issuerid", data.issuer.id);
  formData.append("inwarddividendid", data.id);
  formData.append("yearTypeDividend", excelInfo.dividendYear);

  try {
    const res = await axiosInstance.post(
      "/investor-informations",
      formData
    );

    setIsLoading(false);

    if (res.data.payload.noofentry === 0) {
      setExcelFileError(true);
      return;
    }

    setIsSuccess(true);

    Swal.fire({
      icon: "success",
      title: "Investor information uploaded successfully.",
    });
  } catch (err) {
    setIsLoading(false);
    setIsError(true);

    Swal.fire({
      icon: "error",
      title: "Upload Failed",
      text:
        err.response?.data?.message ||
        "Something went wrong while uploading.",
    });
  }
};
  

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1565c0",
          }}
        >
          Investor Information Upload
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Dividend Year</InputLabel>

          <Select
            label="Dividend Year"
            value={excelInfo.dividendYear}
            onChange={(e) =>
              setExcelInfo((prev) => ({
                ...prev,
                dividendYear: e.target.value,
              }))
            }
          >
            {listOfYear.map((item) => (
              <MenuItem
                key={item.id}
                value={item.name}
              >
                {item.description} {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth>
          <InputLabel>Dividend Type</InputLabel>

          <Select
            label="Dividend Type"
            value={excelInfo.dividendType}
            onChange={(e) =>
              setExcelInfo((prev) => ({
                ...prev,
                dividendType: e.target.value,
              }))
            }
          >
            {dividendOptions.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
              >
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card
          variant="outlined"
          sx={{
            border: "2px dashed #1976d2",
          }}
        >
          <CardContent
            sx={{
              textAlign: "center",
              py: 6,
            }}
          >
            <InsertDriveFileIcon
              sx={{
                fontSize: 60,
                color: "#2e7d32",
              }}
            />

            <Typography mt={2}>
              Upload Investor Excel
            </Typography>

            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{
                mt: 3,
              }}
            >
              Browse

              <input
                hidden
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
              />
            </Button>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6">
              Uploaded File
            </Typography>

            {file ? (
              <>
                <Typography mt={2}>
                  <strong>Name:</strong> {file.name}
                </Typography>

                <Typography>
                  <strong>Size:</strong>{" "}
                  {(file.size / 1024).toFixed(2)} KB
                </Typography>

                <Typography mt={1}>
                  <strong>Preview Rows:</strong>{" "}
                  {previewData.length}
                </Typography>

                <Box
                  mt={2}
                  display="flex"
                  gap={2}
                >
                  {isLoading && (
                                <Chip
                                    color="warning"
                                    label="Uploading..."
                                />
                            )}

                            {isSuccess && (
                                <Chip
                                    color="success"
                                    label="Uploaded Successfully"
                                />
                            )}

                            {excelFileError && (
                                <Chip
                                    color="error"
                                    label="Excel contains no data"
                                />
                            )}

                            {isError && (
                                <Chip
                                    color="error"
                                    label="Upload Failed"
                                />
                            )}


                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={removeFile}
                  >
                    Remove
                  </Button>
                </Box>
              </>
            ) : (
              <Typography mt={2}>
                No Excel file selected.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {previewData.length > 0 && (
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              maxHeight: 450,
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                p: 1,
                bgcolor: "#1976d2",
                color: "#fff",
              }}
            >
              <Typography variant="h6">
                Excel Preview (First 100 Rows)
              </Typography>
            </Box>

            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {headers.map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          fontWeight: "bold",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      {headers.map((head) => (
                        <TableCell key={head}>
                          {row[head]?.toString()}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}